import React, { useState } from 'react';
import { PageHeader, useReveal, GlowDivider } from '../components/Shared';

const NEWS = [
  { id:1, category:'Grant',       date:'Mar 2025', title:'D2S Lab Awarded $4.2M DOE Grant for Battery Materials Acceleration', excerpt:'The Department of Energy has awarded D2S Lab a major grant to accelerate solid-state battery electrolyte discovery using our autonomous pipeline.', color:'teal',   readTime:'3 min' },
  { id:2, category:'Publication', date:'Feb 2025', title:'Nature Materials: Autonomous Discovery of High-Performance Solid Electrolytes', excerpt:'Our latest paper demonstrates a 10× reduction in discovery time for solid electrolytes using closed-loop robotic experimentation and graph neural networks.', color:'purple', readTime:'5 min' },
  { id:3, category:'Partnership', date:'Jan 2025', title:'Strategic Partnership with National Renewable Energy Laboratory', excerpt:'D2S Lab and NREL formalize a 3-year collaboration to accelerate perovskite solar cell commercialization through our shared autonomous platform.', color:'pink',   readTime:'2 min' },
  { id:4, category:'Award',       date:'Dec 2024', title:'Dr. Sarah Chen Named to MIT Technology Review Innovators Under 35', excerpt:'Our PI, Dr. Sarah Chen, has been recognized by MIT Technology Review for her pioneering work in autonomous materials discovery systems.', color:'teal',   readTime:'2 min' },
  { id:5, category:'Publication', date:'Nov 2024', title:'ACS Nano: Graph Neural Networks for 2D Material Energy Storage Screening', excerpt:'We report a new GNN architecture that screens over 50,000 2D material candidates for energy storage applications with DFT-level accuracy at fraction of the cost.', color:'purple', readTime:'6 min' },
  { id:6, category:'Event',       date:'Oct 2024', title:'D2S Lab Presents at MRS Fall Meeting 2024', excerpt:'Three talks and two posters from our team highlight our latest advances in autonomous synthesis, AI-driven discovery, and scale-up engineering.', color:'pink',   readTime:'2 min' },
  { id:7, category:'Hiring',      date:'Sep 2024', title:'Open Positions: Two Postdoctoral Researchers & One Research Engineer', excerpt:'D2S Lab is expanding. We are seeking talented postdoctoral researchers in computational materials science and a full-stack research software engineer.', color:'teal',   readTime:'1 min' },
  { id:8, category:'Grant',       date:'Aug 2024', title:'NSF CAREER Award for Autonomous Multi-Scale Modeling Framework', excerpt:'The National Science Foundation has awarded a CAREER grant to support the development of our next-generation multi-scale modeling infrastructure.', color:'purple', readTime:'3 min' },
];

const CATS = ['All','Grant','Publication','Partnership','Award','Event','Hiring'];
const colorMap  = { teal:'#00ffff', purple:'#a78bfa', pink:'#f9a8d4' };
const bgMap     = { teal:'rgba(0,255,255,0.07)', purple:'rgba(139,92,246,0.08)', pink:'rgba(244,114,182,0.07)' };
const borderMap = { teal:'rgba(0,255,255,0.16)', purple:'rgba(139,92,246,0.2)',  pink:'rgba(244,114,182,0.16)' };

export default function News() {
  useReveal();
  const [filter, setFilter] = useState('All');
  const [expanded, setExpanded] = useState(null);
  const filtered = filter === 'All' ? NEWS : NEWS.filter(n => n.category === filter);

  return (
    <>
      <PageHeader
        tag="News & Updates"
        title="Latest From"
        accent="D2S Lab"
        sub="Stay up-to-date with our latest publications, grants, partnerships, and announcements."
      />

      <GlowDivider />

      <section className="section">
        <div className="container">
          {/* Featured / Hero news item */}
          <div className="glass-card reveal mb-12 overflow-hidden" style={{position:'relative'}}>
            <div className="absolute top-0 left-0 right-0 h-[2px]"
              style={{background:'linear-gradient(90deg,transparent,#00ffff,#8b5cf6,transparent)'}} />
            <div className="p-10 md:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs tracking-[3px] uppercase px-3 py-1 rounded-full"
                    style={{color:'#00ffff',background:'rgba(0,255,255,0.08)',border:'1px solid rgba(0,255,255,0.2)'}}>Featured · {NEWS[0].category}</span>
                  <span className="font-mono text-xs" style={{color:'var(--text-3)'}}>{NEWS[0].date}</span>
                </div>
                <h2 className="font-display font-bold text-[1.7rem] tracking-[-0.04em] leading-tight mb-4">{NEWS[0].title}</h2>
                <p className="text-sm font-light leading-relaxed mb-7" style={{color:'var(--text-2)'}}>{NEWS[0].excerpt}</p>
                <a href="#" className="btn btn--primary">Read Full Story</a>
              </div>
              <div className="relative h-48 lg:h-64 rounded-xl overflow-hidden"
                style={{background:'linear-gradient(135deg,rgba(0,255,255,0.06) 0%,rgba(139,92,246,0.06) 100%)',border:'1px solid var(--border)'}}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-display font-extrabold text-[4rem] tracking-[-0.05em] mb-2"
                      style={{background:'linear-gradient(135deg,#00ffff,#a78bfa)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text'}}>
                      $4.2M
                    </div>
                    <p className="font-mono text-xs tracking-[3px] uppercase" style={{color:'var(--text-3)'}}>DOE Grant Awarded</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter */}
          <div className="flex flex-wrap gap-2 mb-10 reveal">
            {CATS.map(c => (
              <button key={c} onClick={() => setFilter(c)}
                className="font-mono text-xs tracking-[2px] uppercase px-4 py-2 rounded-full border transition-all duration-300 cursor-pointer"
                style={{
                  fontFamily:'var(--font-mono)',
                  background: filter===c ? 'rgba(0,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                  color:      filter===c ? '#00ffff' : 'var(--text-2)',
                  borderColor:filter===c ? 'rgba(0,255,255,0.25)' : 'var(--border)',
                }}>
                {c}
              </button>
            ))}
          </div>

          {/* News grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((item,i) => (
              <div key={item.id} className="glass-card reveal cursor-pointer group"
                style={{padding:'32px 28px',transitionDelay:`${i*0.07}s`,position:'relative'}}
                onClick={() => setExpanded(expanded===item.id ? null : item.id)}>
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
                  style={{background:`linear-gradient(90deg,transparent,${colorMap[item.color]},transparent)`}} />

                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[0.62rem] tracking-[2px] uppercase px-2.5 py-1 rounded-full"
                    style={{color:colorMap[item.color],background:bgMap[item.color],border:`1px solid ${borderMap[item.color]}`}}>
                    {item.category}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs" style={{color:'var(--text-3)'}}>{item.date}</span>
                    <span className="font-mono text-[0.6rem]" style={{color:'var(--text-3)'}}>{item.readTime} read</span>
                  </div>
                </div>

                <h3 className="font-display font-bold text-base tracking-[-0.02em] leading-snug mb-3 transition-colors duration-300"
                  style={{color: expanded===item.id ? colorMap[item.color] : 'var(--text-1)'}}>
                  {item.title}
                </h3>

                <div className={`overflow-hidden transition-all duration-500 ${expanded===item.id ? 'max-h-40 opacity-100 mb-5' : 'max-h-0 opacity-0'}`}>
                  <p className="text-sm font-light leading-relaxed" style={{color:'var(--text-2)'}}>{item.excerpt}</p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t" style={{borderColor:'var(--border)'}}>
                  <span className="text-xs font-light" style={{color:'var(--text-3)'}}>
                    {expanded===item.id ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <svg className={`w-4 h-4 transition-transform duration-300 ${expanded===item.id ? 'rotate-180' : ''}`}
                    style={{color:colorMap[item.color]}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Newsletter */}
      <section className="section section-alt">
        <div className="container">
          <div className="glass-card reveal text-center p-14" style={{position:'relative'}}>
            <div className="absolute inset-0 rounded-[18px] pointer-events-none"
              style={{background:'radial-gradient(ellipse at center,rgba(139,92,246,0.04) 0%,transparent 70%)'}} />
            <span className="section-tag justify-center">Stay Updated</span>
            <h2 className="section-title mb-3">Subscribe to Our <span className="text-gradient">Newsletter</span></h2>
            <p className="text-sm font-light mx-auto mb-8 max-w-[400px]" style={{color:'var(--text-2)'}}>
              Get monthly updates on our latest research, publications, and open positions.
            </p>
            <div className="flex gap-3 justify-center max-w-md mx-auto flex-wrap">
              <input type="email" placeholder="your@email.com"
                className="flex-1 min-w-[200px] px-5 py-3 rounded-full text-sm font-light outline-none transition-all duration-300"
                style={{background:'rgba(255,255,255,0.05)',border:'1px solid var(--border)',color:'var(--text-1)',fontFamily:'var(--font-body)'}}
                onFocus={e=>{e.currentTarget.style.borderColor='rgba(0,255,255,0.35)';e.currentTarget.style.boxShadow='0 0 20px rgba(0,255,255,0.08)'}}
                onBlur={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.boxShadow='none'}}
              />
              <button className="btn btn--primary !py-3 !px-7 !text-sm">Subscribe</button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
